/**
 * COR-125 — the catch-all neighborhood bucket, labelled "Greater <City>"
 * (WEB-1230, ruling R9 of the Discovery PRD; it read "Elsewhere in <City>"
 * before). The slug stays `elsewhere` so existing links and filters keep
 * working; only the label changed.
 *
 * Roughly 4 in 10 upcoming CLT events sit at venues outside every authored
 * neighborhood polygon. Before this bucket existed those events could appear
 * on no neighborhood surface at all, and the Discover neighborhood facet
 * silently summed to far less than `meta.total`. The operator decision was a
 * catch-all bucket on the neighborhood surfaces rather than new polygons, so
 * every surface that lists neighborhoods can offer one more entry that
 * reconciles its counts.
 *
 * `elsewhere` is a RESERVED filter value, not a neighborhood:
 *
 * - it never has a row in the `neighborhoods` table, a polygon, a centroid, a
 *   color token, or a `/neighborhoods/<slug>` page — so it must never be added
 *   to a slug list that feeds page routing, the sitemap, or the map;
 * - each surface resolves it against its OWN membership rule. The Discover
 *   event facet uses the free-text `Location.neighborhood` column (elsewhere =
 *   no label, or no venue at all); venue browse uses `ST_Within` containment
 *   (elsewhere = inside no polygon). Unifying those two definitions is
 *   deliberately out of scope here.
 *
 * Lives in `@loci/types` because it is the lowest layer every consumer
 * (`db-core`, `backend-api`, `web`) already depends on. The city display name
 * comes from `CITY_RECORDS` in `@loci/city-editions`; callers pass it in so
 * this module stays dependency-free.
 */

/** URL / facet slug for the catch-all bucket. Reserved: no real neighborhood uses it. */
export const ELSEWHERE_NEIGHBORHOOD_SLUG = 'elsewhere' as const;

/**
 * True when `value` names the catch-all bucket. Case- and padding-insensitive,
 * matching how the Discover backend normalizes `?neighborhood=` values.
 */
export function isElsewhereNeighborhoodSlug(value: string | null | undefined): boolean {
  if (typeof value !== 'string') return false;
  return value.trim().toLowerCase() === ELSEWHERE_NEIGHBORHOOD_SLUG;
}

/**
 * Display label for the catch-all bucket — `Greater Charlotte` for CLT
 * (WEB-1230, ruling R9).
 *
 * Pass the city's `displayName` from `getCityRecord(code)` rather than a
 * literal, so a new market labels itself. Falls back to plain `Elsewhere` when
 * the city is unknown, which is still true and never names the wrong city.
 */
export function formatElsewhereNeighborhoodLabel(
  cityDisplayName: string | null | undefined,
): string {
  const name = typeof cityDisplayName === 'string' ? cityDisplayName.trim() : '';
  return name.length > 0 ? `Greater ${name}` : 'Elsewhere';
}
